'use client';

import { useState, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Pick } from '@/lib/supabase';
import type { SportFilter, BetTypeFilter } from './ReportingFilters';

function formatOdds(odds: number): string {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

type SortKey = 'posted_at' | 'sport' | 'odds' | 'units' | 'units_result';
type SortDir = 'asc' | 'desc';

interface Props {
  sport: SportFilter;
  betType: BetTypeFilter;
}

export default function PicksHistoryTable({ sport, betType }: Props) {
  const [allPicks, setAllPicks] = useState<Pick[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('posted_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    async function loadPicks() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('*')
          .neq('result', 'pending');

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);

        const { data, error } = await query.order('posted_at', { ascending: false });
        if (error) throw error;
        setAllPicks(data || []);
        setPage(1);
      } catch {
        setAllPicks([]);
      } finally {
        setLoading(false);
      }
    }
    loadPicks();
  }, [sport, betType]);

  const filtered = allPicks.filter((p) => {
    if (!search) return true;
    return (
      p.game.toLowerCase().includes(search.toLowerCase()) ||
      p.pick.toLowerCase().includes(search.toLowerCase()) ||
      p.sport.toLowerCase().includes(search.toLowerCase())
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal: number | string, bVal: number | string;
    if (sortKey === 'posted_at') { aVal = a.posted_at; bVal = b.posted_at; }
    else if (sortKey === 'sport') { aVal = a.sport; bVal = b.sport; }
    else if (sortKey === 'odds') { aVal = a.odds; bVal = b.odds; }
    else if (sortKey === 'units') { aVal = a.units; bVal = b.units; }
    else { aVal = a.units_result || 0; bVal = b.units_result || 0; }

    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  function SortIcon({ k }: { k: SortKey }) {
    if (sortKey !== k) return <ChevronsUpDown size={12} className="text-[hsl(var(--muted-foreground))]" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-[hsl(var(--primary))]" /> : <ChevronDown size={12} className="text-[hsl(var(--primary))]" />;
  }

  const resultBadge = (result: string) => {
    const map: Record<string, string> = {
      win: 'result-win',
      loss: 'result-loss',
      push: 'result-push',
      pending: 'result-pending',
      void: 'bg-gray-500/10 text-gray-400 border border-gray-500/20',
    };
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[result] || ''}`;
  };

  return (
    <div className="stat-card p-0 overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[hsl(var(--border))]">
        <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Picks History</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))]" />
            <input
              type="text"
              placeholder="Search picks..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input-field pl-9 w-48 text-sm"
            />
          </div>
          <span className="text-xs text-[hsl(var(--foreground-muted))] font-mono">{filtered.length} results</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading picks...</span>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                {[
                  { label: 'Date', key: 'posted_at' as SortKey },
                  { label: 'Sport', key: 'sport' as SortKey },
                  { label: 'Game', key: null },
                  { label: 'Pick', key: null },
                  { label: 'Type', key: null },
                  { label: 'Odds', key: 'odds' as SortKey },
                  { label: 'Units', key: 'units' as SortKey },
                  { label: 'Result', key: null },
                  { label: 'P&L', key: 'units_result' as SortKey },
                ].map(({ label, key }) => (
                  <th
                    key={label}
                    onClick={key ? () => toggleSort(key) : undefined}
                    className={`px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] ${key ? 'cursor-pointer hover:text-[hsl(var(--foreground))] select-none' : ''}`}
                  >
                    <div className="flex items-center gap-1.5">{label}{key && <SortIcon k={key} />}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-[hsl(var(--foreground-muted))] text-sm">
                    No picks found matching your filters.
                  </td>
                </tr>
              ) : (
                paginated.map((pick, i) => (
                  <tr key={pick.id} className={`border-b border-[hsl(var(--border)/0.5)] hover:bg-[hsl(var(--card-elevated))] transition-colors duration-100 ${i % 2 === 0 ? '' : 'bg-[hsl(var(--card-elevated)/0.3)]'}`}>
                    <td className="px-5 py-3.5"><span className="font-mono text-xs text-[hsl(var(--foreground-muted))]">{formatDate(pick.posted_at)}</span></td>
                    <td className="px-5 py-3.5"><span className="text-sm font-medium text-[hsl(var(--foreground))]">{pick.sport}</span></td>
                    <td className="px-5 py-3.5"><span className="text-sm text-[hsl(var(--foreground-muted))] max-w-[160px] block truncate">{pick.game}</span></td>
                    <td className="px-5 py-3.5"><span className="text-sm font-semibold text-[hsl(var(--foreground))]">{pick.pick}</span></td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--card-elevated))] text-[hsl(var(--foreground-muted))] border border-[hsl(var(--border))] capitalize">{pick.bet_type}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`font-mono text-sm font-semibold tabular-nums ${pick.odds > 0 ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--foreground))]'}`}>{formatOdds(pick.odds)}</span>
                    </td>
                    <td className="px-5 py-3.5"><span className="font-mono text-sm tabular-nums text-[hsl(var(--foreground))]">{pick.units}u</span></td>
                    <td className="px-5 py-3.5">
                      <span className={resultBadge(pick.result)}>{pick.result.charAt(0).toUpperCase() + pick.result.slice(1)}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`font-mono font-bold text-sm tabular-nums ${
                        (pick.units_result || 0) > 0 ? 'text-[hsl(var(--primary))]' :
                        (pick.units_result || 0) < 0 ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        {pick.units_result !== null && pick.units_result !== undefined
                          ? `${pick.units_result >= 0 ? '+' : ''}${pick.units_result.toFixed(2)}u`
                          : '0.00u'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-4 border-t border-[hsl(var(--border))]">
          <span className="text-xs text-[hsl(var(--foreground-muted))] font-mono">Page {page} of {totalPages} · {filtered.length} picks</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40">Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = i + 1;
              return (
                <button key={p} onClick={() => setPage(p)} className={`w-8 h-8 rounded-lg text-xs font-mono font-medium transition-all duration-150 ${page === p ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--card-elevated))] hover:text-[hsl(var(--foreground))]'}`}>{p}</button>
              );
            })}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}